/* CleanlinessMonitorDialog — the "Live Cleanliness Monitor" screen.
   Reuses the plain resort/floor maps (slides) but strips reconstruction
   detail: each area shows only its exact name + a cleanliness status
   dot. Any signed-in user can update a status, but a PHOTO is mandatory
   (captured/selected, downscaled to base64, sent to PATCH /api/cleanliness).
   Polls every 30s so it stays "live". */
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, RefreshCw, Camera, Bell, Loader2, Check, Plus } from 'lucide-react';
import { RESORT_MAP_URL, HOTEL_PLAN_URL, FLOOR_PLAN_EXTENDED_URL, BACK_OF_HOUSE_URL } from '../data/resortAreas';
import { isAdminLike } from '../lib/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_IMAGES = { 'back-of-house': BACK_OF_HOUSE_URL, indoor: HOTEL_PLAN_URL, rooftop: FLOOR_PLAN_EXTENDED_URL, outdoor: RESORT_MAP_URL };

// Cleanliness-monitor-only floor screens (3rd/5th/6th/7th). These are NOT
// part of the shared /slides set, so they never appear on the main
// Interactive Map. Per request they reuse the 4th-floor plan image as a
// placeholder until real floor plans are uploaded.
const EXTRA_CLEANLINESS_FLOORS = [
  { id: 'clean-floor-3', name: '3rd Floor', zone: 'floor-3', image_url: FLOOR_PLAN_EXTENDED_URL },
  { id: 'clean-floor-5', name: '5th Floor', zone: 'floor-5', image_url: FLOOR_PLAN_EXTENDED_URL },
  { id: 'clean-floor-6', name: '6th Floor', zone: 'floor-6', image_url: FLOOR_PLAN_EXTENDED_URL },
  { id: 'clean-floor-7', name: '7th Floor', zone: 'floor-7', image_url: FLOOR_PLAN_EXTENDED_URL },
];

const STATUS_META = {
  clean: { label: 'Clean', color: '#22C55E' },
  in_progress: { label: 'In progress', color: '#3B82F6' },
  needs_attention: { label: 'Needs attention', color: '#F59E0B' },
  overdue: { label: 'Overdue', color: '#EF4444' },
  unrecorded: { label: 'Not recorded', color: '#94A3B8' },
};

// Downscale a selected image to <=1000px and return a JPEG data URL.
function downscale(file, maxDim = 1000, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = (height * maxDim) / width; width = maxDim; }
        else if (height > maxDim) { width = (width * maxDim) / height; height = maxDim; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function CleanlinessMonitorDialog({ open, onClose, slides, settings }) {
  const [data, setData] = useState({ areas: [], counts: {}, sla_hours: 24 });
  const [loading, setLoading] = useState(true);
  const [activeZone, setActiveZone] = useState(null);
  const [selected, setSelected] = useState(null); // area widget being edited
  const [form, setForm] = useState({ status: 'clean', note: '', photo: '' });
  const [saving, setSaving] = useState(false);
  const [alarming, setAlarming] = useState(false);
  const [manageMode, setManageMode] = useState(false);
  const [newWidget, setNewWidget] = useState(null); // { position_x, position_y }
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const pollRef = useRef(null);
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const isAdmin = isAdminLike(typeof window !== 'undefined' ? localStorage.getItem('user_role') : null);
  const auth = { headers: token ? { Authorization: `Bearer ${token}` } : {} };

  const usableSlides = [
    ...(slides || []).filter((s) => s.image_url || FALLBACK_IMAGES[s.zone]),
    ...EXTRA_CLEANLINESS_FLOORS,
  ];

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/cleanliness`, auth);
      setData(d);
    } catch {
      /* keep last */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    load();
    pollRef.current = setInterval(load, 30000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [open, load]);

  useEffect(() => {
    if (open && !activeZone && usableSlides.length) setActiveZone(usableSlides[0].zone);
  }, [open, usableSlides, activeZone]);

  const statusByArea = {};
  data.areas.forEach((a) => { statusByArea[a.area_id] = a; });

  const openWidget = (area) => {
    const rec = statusByArea[area.area_id] || {};
    setSelected(area);
    setForm({ status: rec.status && rec.status !== 'unrecorded' ? rec.status : 'clean', note: rec.note || '', photo: '' });
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await downscale(file);
      setForm((f) => ({ ...f, photo: dataUrl }));
    } catch {
      toast.error('Could not read that image.');
    }
  };

  const submit = async () => {
    if (!form.photo) { toast.error('A photo is required to change the status.'); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/cleanliness/${selected.area_id}`, { status: form.status, note: form.note, photo: form.photo }, auth);
      toast.success(`${selected.name} → ${STATUS_META[form.status].label}`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const sendAlarm = async () => {
    setAlarming(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/cleanliness/send-eod-alarm`, {}, auth);
      if (r.flagged === 0) toast.success('All clear — nothing to alarm.');
      else if (r.whatsapp_enabled && r.recipients > 0) toast.success(`Alarm sent for ${r.flagged} area(s) to ${r.recipients} recipient(s).`);
      else toast.info(`${r.flagged} area(s) need attention. WhatsApp not configured yet — logged only.`);
    } catch {
      toast.error('Could not send alarm.');
    } finally {
      setAlarming(false);
    }
  };

  // Admin: tap the map (in manage mode) to drop a new cleaning point.
  const handleMapClick = (e) => {
    if (!manageMode || !isAdmin) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setNewWidget({ position_x: Math.round(x * 10) / 10, position_y: Math.round(y * 10) / 10 });
    setNewName('');
  };

  const createWidget = async () => {
    if (!newName.trim()) { toast.error('Give the cleaning point a name.'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/cleanliness/widgets`, {
        name: newName.trim(), zone: activeZone,
        position_x: newWidget.position_x, position_y: newWidget.position_y,
      }, auth);
      toast.success('Cleaning point added.');
      setNewWidget(null); setNewName('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add the cleaning point.');
    } finally {
      setCreating(false);
    }
  };

  const removeWidget = async (id) => {
    try {
      await axios.delete(`${API}/admin/cleanliness/widgets/${id}`, auth);
      toast.success('Cleaning point removed.');
      load();
    } catch {
      toast.error('Could not remove the cleaning point.');
    }
  };

  if (!open) return null;
  const activeSlide = usableSlides.find((s) => s.zone === activeZone);
  const activeAreas = data.areas.filter((a) => a.zone === activeZone && a.position_x != null && a.position_y != null);
  const c = data.counts || {};
  const attention = (c.needs_attention || 0) + (c.in_progress || 0) + (c.overdue || 0);

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex flex-col" style={{ backgroundColor: '#0A1A2F', fontFamily: 'Manrope' }} data-testid="cleanliness-monitor">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,.1)' }}>
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
          <h2 className="text-white text-lg font-bold">Live Cleanliness Monitor</h2>
          {attention > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white" data-testid="cleanliness-attention-count">{attention} need attention</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10" title="Refresh" data-testid="cleanliness-refresh"><RefreshCw className="w-4 h-4" /></button>
          {isAdmin && (
            <button onClick={() => setManageMode((v) => !v)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold shadow-md transition-all ${manageMode ? 'bg-white text-[#0A1A2F]' : 'text-white hover:brightness-110'}`} style={manageMode ? {} : { backgroundColor: '#16A34A' }} data-testid="cleanliness-manage-toggle">
              {manageMode ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {manageMode ? 'Done' : 'Add / Remove Points'}
            </button>
          )}
          {isAdmin && (
            <button onClick={sendAlarm} disabled={alarming} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50" data-testid="cleanliness-send-alarm">
              {alarming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} End-of-day alarm
            </button>
          )}
          <button onClick={onClose} className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10" data-testid="cleanliness-close"><X className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Legend + tabs */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-2 border-b" style={{ borderColor: 'rgba(255,255,255,.08)' }}>
        {Object.entries(STATUS_META).map(([k, m]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: m.color }} />
            <span className="text-[11px] text-white/70 font-semibold">{m.label} {c[k] ? `(${c[k]})` : ''}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2 px-5 py-2 overflow-x-auto border-b" style={{ borderColor: 'rgba(255,255,255,.08)' }}>
        {usableSlides.map((s) => (
          <button key={s.id || s.zone} onClick={() => setActiveZone(s.zone)} data-testid={`cleanliness-tab-${s.zone}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${activeZone === s.zone ? 'bg-white text-[#0A1A2F]' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
            {s.name}
          </button>
        ))}
      </div>

      {/* Map + widgets — sized to fit fully within the screen (no scroll). */}
      <div className="flex-1 min-h-0 overflow-hidden p-3 flex flex-col">
        {manageMode && isAdmin && (
          <div className="mx-auto mb-2 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'rgba(59,130,246,.15)', color: '#BFDBFE' }} data-testid="cleanliness-manage-hint">
            <Plus className="w-4 h-4" /> Tap anywhere on the map to add a cleaning point. Tap the × on a point to remove it.
          </div>
        )}
        <div className="flex-1 min-h-0 flex items-center justify-center">
        {loading ? (
          <div className="flex items-center justify-center h-full text-white/60"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : activeSlide ? (
          <div
            className="relative rounded-xl overflow-hidden"
            data-testid="cleanliness-map"
            onClick={handleMapClick}
            style={{ cursor: manageMode && isAdmin ? 'crosshair' : 'default', display: 'inline-block', maxWidth: '100%', maxHeight: '100%' }}
          >
            <img
              src={activeSlide.image_url || FALLBACK_IMAGES[activeSlide.zone]}
              alt={activeSlide.name}
              className="block select-none"
              style={{ maxWidth: 'calc(100vw - 48px)', maxHeight: manageMode && isAdmin ? 'calc(100vh - 250px)' : 'calc(100vh - 190px)', width: 'auto', height: 'auto' }}
            />
            {activeAreas.map((a) => {
              const m = STATUS_META[a.status] || STATUS_META.unrecorded;
              const pulse = a.status === 'overdue' || a.status === 'needs_attention';
              return (
                <div key={a.area_id} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${a.position_x}%`, top: `${a.position_y}%` }}>
                  <button data-testid={`cleanliness-widget-${a.area_id}`} onClick={(e) => { e.stopPropagation(); openWidget(a); }}
                    className="flex items-center gap-1 rounded-full pl-1 pr-2 py-1 shadow-lg hover:scale-110 transition-transform"
                    style={{ backgroundColor: 'rgba(255,255,255,.95)', border: `2px solid ${m.color}` }}>
                    <span className={`w-3 h-3 rounded-full ${pulse ? 'animate-pulse' : ''}`} style={{ backgroundColor: m.color }} />
                    <span className="text-[10px] font-bold" style={{ color: '#0A1A2F' }}>{a.name}</span>
                  </button>
                  {manageMode && isAdmin && (
                    <button data-testid={`cleanliness-remove-${a.area_id}`} onClick={(e) => { e.stopPropagation(); removeWidget(a.area_id); }} title="Remove cleaning point"
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center shadow-md hover:bg-red-700">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
            {activeAreas.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'rgba(10,26,47,.75)', color: '#E2E8F0' }} data-testid="cleanliness-empty-zone">
                  {isAdmin ? 'No cleaning points on this screen yet — tap “Add / Remove Points”, then tap the map to add one.' : 'No cleaning points configured for this screen yet.'}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-white/60">No maps configured.</p>
        )}
        </div>
      </div>

      {/* New widget sheet */}
      {newWidget && (
        <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => !creating && setNewWidget(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="cleanliness-new-widget-sheet">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">New cleaning point</h3>
              <button onClick={() => setNewWidget(null)} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Name</label>
            <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Pool Deck, Lobby Restroom" className="w-full mt-1.5 mb-4 px-3 py-2 rounded-lg border text-sm outline-none" style={{ borderColor: '#D6D0C0' }} data-testid="cleanliness-new-widget-name" />
            <button onClick={createWidget} disabled={creating || !newName.trim()} className="w-full py-3 rounded-xl text-white font-bold text-sm disabled:opacity-50" style={{ backgroundColor: settings?.primary_color || '#1F4FBE' }} data-testid="cleanliness-new-widget-save">
              {creating ? 'Adding…' : 'Add cleaning point'}
            </button>
          </div>
        </div>
      )}

      {/* Update sheet */}
      {selected && (
        <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => !saving && setSelected(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="cleanliness-update-sheet">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">{selected.name}</h3>
              <button onClick={() => setSelected(null)} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Status</label>
            <div className="grid grid-cols-2 gap-2 mt-1.5 mb-3">
              {['clean', 'in_progress', 'needs_attention', 'overdue'].map((s) => (
                <button key={s} onClick={() => setForm((f) => ({ ...f, status: s }))} data-testid={`cleanliness-status-${s}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold border-2 ${form.status === s ? '' : 'opacity-60'}`}
                  style={{ borderColor: STATUS_META[s].color, backgroundColor: form.status === s ? `${STATUS_META[s].color}18` : '#fff', color: '#0A1A2F' }}>
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_META[s].color }} /> {STATUS_META[s].label}
                </button>
              ))}
            </div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Note (optional)</label>
            <input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. Spill cleared, awaiting mop dry" className="w-full mt-1.5 mb-3 px-3 py-2 rounded-lg border text-sm outline-none" style={{ borderColor: '#D6D0C0' }} data-testid="cleanliness-note" />
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Photo (required)</label>
            <label className="mt-1.5 flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 border-dashed cursor-pointer text-sm font-bold" style={{ borderColor: form.photo ? '#22C55E' : '#D6D0C0', color: form.photo ? '#15803D' : '#4A5D5D' }} data-testid="cleanliness-photo-label">
              <Camera className="w-4 h-4" /> {form.photo ? 'Photo attached — tap to replace' : 'Take / choose a photo'}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto} data-testid="cleanliness-photo-input" />
            </label>
            {form.photo && <img src={form.photo} alt="preview" className="mt-2 w-full h-32 object-cover rounded-lg" />}
            <button onClick={submit} disabled={saving || !form.photo} className="w-full mt-4 py-3 rounded-xl text-white font-bold text-sm disabled:opacity-50" style={{ backgroundColor: settings?.primary_color || '#1F4FBE' }} data-testid="cleanliness-submit">
              {saving ? 'Saving…' : 'Update status'}
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
